import { OnCallPayInfo, OnCallServiceOrderOption, OnCallTrade } from '../../api/onCallPay';

/**
 * Pure logic behind the On Call $ tile + OnCallPayModal (2020CA / 2020NV, TAS-475/476):
 * amount parsing, the modal's default SO / trade, and the trade auto-follow rule.
 * Kept out of the .tsx so it can be unit-tested without rendering.
 */

export const ON_CALL_TRADE_LABEL: Record<OnCallTrade, string> = {
  PLUMBING: 'Plumbing',
  HVAC: 'HVAC',
};

export type OnCallAmountParse =
  | { ok: true; amount: number | null }
  | { ok: false; error: string };

/** Parse an amount box: `amount: null` = empty / $0 (remove), a number = save. */
export function parseOnCallAmount(raw: string, max: number): OnCallAmountParse {
  const text = String(raw).trim().replace(/[$,\s]/g, '');
  const isZero = /^\d*\.?\d*$/.test(text) && /\d/.test(text) && Number(text) === 0;
  if (text === '' || isZero) return { ok: true, amount: null };
  if (!/^\d*\.?\d*$/.test(text) || !isFinite(Number(text)) || Number(text) <= 0) {
    return { ok: false, error: 'Enter a dollar amount — numbers only (0 removes it).' };
  }
  if (!/^\d*(\.\d{0,2})?$/.test(text)) {
    return { ok: false, error: 'On Call $ allows at most 2 decimal places.' };
  }
  const amount = Math.round(Number(text) * 100) / 100;
  if (amount > max) {
    return { ok: false, error: `Max On Call is $${Math.round(max).toLocaleString('en-US')}` };
  }
  return { ok: true, amount };
}

/** The saved entry, or null when the week has no On Call $ (amount missing / 0). */
export function savedOnCall(info: OnCallPayInfo | undefined): OnCallPayInfo | null {
  return info && info.amount != null && info.amount > 0 ? info : null;
}

/** Opening selection: the saved SO (null = non-job) when an amount is saved, else the
 *  week's most recent SO (options come newest-first), else "No service order". */
export function initialOnCallSo(options: OnCallServiceOrderOption[], saved: OnCallPayInfo | null): number | null {
  if (saved) return saved.serviceOrderID ?? null;
  return options[0]?.serviceOrderID ?? null;
}

/** Opening trade: exactly what is saved (null on a legacy entry), else the default SO's. */
export function initialOnCallTrade(
  options: OnCallServiceOrderOption[],
  saved: OnCallPayInfo | null,
): OnCallTrade | null {
  if (saved) return saved.trade ?? null;
  return tradeForOnCallSo(initialOnCallSo(options, null), options, null);
}

/**
 * The trade an SO pick re-defaults to while the user hasn't touched the toggle: the saved
 * trade when returning to the saved coding (or picking non-job), otherwise the SO's derived
 * trade — null for tub types, and for non-job with nothing saved.
 */
export function tradeForOnCallSo(
  so: number | null,
  options: OnCallServiceOrderOption[],
  saved: OnCallPayInfo | null,
): OnCallTrade | null {
  const savedSo = saved ? saved.serviceOrderID ?? null : undefined;
  if (saved?.trade && so === savedSo) return saved.trade;
  if (so == null) return saved?.trade ?? null;
  return options.find((o) => o.serviceOrderID === so)?.derivedTrade ?? null;
}

/** One-line coding summary for the tile: "SO 88123 · Plumbing" / "No SO · non-job · HVAC". */
export function onCallSummary(saved: OnCallPayInfo): string | null {
  if (!saved.trade) return null;
  const trade = ON_CALL_TRADE_LABEL[saved.trade];
  if (saved.serviceOrderID == null) return `No SO · non-job · ${trade}`;
  return `SO ${saved.altServiceOrderID ?? saved.serviceOrderID} · ${trade}`;
}
